import clsx from 'clsx';
import { useRouter } from 'next/router';
import { Direction } from 'types/Utilities';
import Icon from 'components/ui/Icon';
import Link from 'components/ui/Link';
import { useTranslation } from 'hooks/useTranslation';
import type { ReactNode } from 'react';

type PageNumberProps = {
  num: number;
  children: ReactNode;
  firstIsBase: boolean;
  locale: string;
  base: string;
};

const PageNumber = ({
  num,
  children,
  firstIsBase,
  locale,
  base,
}: PageNumberProps): JSX.Element => {
  if (firstIsBase && num === 1) {
    <Link href={`/${locale}/${base}`}>{children}</Link>;
  }

  return <Link href={`/${locale}/${base}/page/${num}`}>{children}</Link>;
};

type PaginationProps = {
  base: string;
  pages: number;
  firstIsBase: boolean;
};

const Pagination = ({
  base,
  pages,
  firstIsBase = false,
}: PaginationProps): JSX.Element => {
  const router = useRouter();
  const { locale } = useTranslation();
  const page = router.query.page ? parseInt(router.query.page as string) : 1;

  return (
    <div className="mx-auto flex justify-center mt-20">
      {pages > 1 && page > 1 && (
        <Link href={`/${locale}/${base}`}>
          <a className="rounded-full border-2 border-primary text-secondary w-12 h-12 mx-2 flex items-center justify-center hover:bg-primary hover:text-white transition-color duration-300 ease-in-out">
            <Icon type="Angle" direction={Direction.LEFT} />
          </a>
        </Link>
      )}
      {[...Array(pages).keys()].map((i) => {
        const num = i + 1;
        return (
          <PageNumber
            key={i}
            num={num}
            firstIsBase={firstIsBase}
            locale={locale}
            base={base}
          >
            <a
              className={clsx(
                page == num ? 'text-white bg-primary' : 'text-secondary',
                num > page + 1 || num < page - 1 ? 'hidden' : '',
                'rounded-full border-2 font-bold border-primary text-xl w-12 h-12 text-center mx-2 flex items-center justify-center hover:bg-primary hover:text-white transition-color duration-300 ease-in-out',
              )}
            >
              {num}
            </a>
          </PageNumber>
        );
      })}
      {pages > 1 && page < pages && (
        <Link href={`/${locale}/${base}/page/${page + 1}`}>
          <a className="rounded-full border-2 border-primary text-secondary w-12 h-12 mx-2 flex items-center justify-center hover:bg-primary hover:text-white transition-color duration-300 ease-in-out">
            <Icon type="Angle" />
          </a>
        </Link>
      )}
    </div>
  );
};

export default Pagination;
